import { useState, useEffect, type Dispatch, type SetStateAction } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import {
  Play,
  Save,
  Rocket,
  Pause,
  Copy,
  History,
  Zap,
  Cog,
  Send,
  Check,
  Lock,
  ShieldAlert,
  CircleDot,
  PanelRightClose,
  PanelRightOpen,
  Maximize2,
  Minimize2,
} from "lucide-react";
import { useWorkflow, validateWorkflow, permissionFootprint, workflowGatedProducts } from "@/store/workflowStore";
import { useExecution } from "@/store/executionStore";
import { useHive } from "@/store/useHive";
import { WorkflowLibrary } from "./WorkflowLibrary";
import { WorkflowCanvas } from "./WorkflowCanvas";
import { NodePalette } from "./NodePalette";
import { NodeInspector } from "./NodeInspector";
import { WorkflowBottomDrawer } from "./WorkflowBottomDrawer";
import { WorkflowTemplatesModal } from "./WorkflowTemplatesModal";
import { Modal } from "@/components/ui/Modal";
import { SampleBadge } from "@/components/ui/SampleBadge";
import { APPS } from "@/data/apps";
import { WF_STATUS_META, RiskBadge, Pill, Segmented } from "@/components/hive/shared";
import type { HiveWorkflow, WfNodeKind } from "@/types";

export function WorkflowBuilderWindow() {
  const workflows = useWorkflow((s) => s.workflows);
  const selectedId = useWorkflow((s) => s.selectedWorkflowId);
  const wf = workflows.find((w) => w.id === selectedId) ?? workflows[0];
  const [paletteKind, setPaletteKind] = useState<WfNodeKind | null>(null);
  const [inspectorOpen, setInspectorOpen] = useState(true);
  const [fullscreen, setFullscreen] = useState(false);

  useEffect(() => {
    if (!fullscreen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setFullscreen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [fullscreen]);

  // Fresh orgs have no workflows yet — offer the two ways to start one.
  if (!wf) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 bg-[hsl(var(--bg-1))] p-8 text-center">
        <div className="grid h-12 w-12 place-items-center rounded-2xl text-white shadow-lg" style={{ background: "linear-gradient(135deg, #f43f5e, #be123c)" }}>
          <Zap size={22} />
        </div>
        <div className="text-sm font-semibold text-app">No workflows yet</div>
        <p className="max-w-xs text-xs leading-relaxed text-app-2">
          Automate your delivery pipeline with a Hook → Process → Publish flow. Start from
          scratch or pick a template.
        </p>
        <div className="mt-2 flex items-center gap-2">
          <button
            onClick={() => useWorkflow.getState().newWorkflow()}
            className="rounded-lg px-3.5 py-2 text-xs font-semibold text-white transition hover:brightness-110"
            style={{ background: "#f43f5e" }}
          >
            New workflow
          </button>
          <button
            onClick={() => useWorkflow.getState().setTemplatesOpen(true)}
            className="rounded-lg border border-app bg-surface-2/60 px-3.5 py-2 text-xs font-medium text-app-1 transition hover:border-app-strong"
          >
            Browse templates
          </button>
        </div>
        <WorkflowTemplatesModal />
      </div>
    );
  }

  const area = { wf, paletteKind, setPaletteKind, inspectorOpen, setInspectorOpen };

  return (
    <div className="flex h-full flex-col bg-[hsl(var(--bg-1))]">
      <TopBar wf={wf} onAdd={setPaletteKind} />
      <div className="flex min-h-0 flex-1">
        <WorkflowLibrary />
        <div className="flex min-w-0 flex-1 flex-col">
          <BuilderCanvasArea {...area} onToggleFullscreen={() => setFullscreen(true)} />
          <WorkflowBottomDrawer wf={wf} />
        </div>
      </div>
      <WorkflowTemplatesModal />
      <PublishModal />

      {createPortal(
        <AnimatePresence>
          {fullscreen && (
            <motion.div
              initial={{ opacity: 0, scale: 0.985 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.985 }}
              transition={{ type: "spring", stiffness: 320, damping: 32 }}
              className="fixed inset-0 z-[90] flex flex-col bg-[hsl(var(--bg-0))]"
            >
              <FullscreenHeader wf={wf} onAdd={setPaletteKind} onExit={() => setFullscreen(false)} />
              <BuilderCanvasArea {...area} fullscreen onToggleFullscreen={() => setFullscreen(false)} />
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </div>
  );
}

/* ── Canvas + palette + collapsible inspector (shared by window & fullscreen) ── */

interface AreaProps {
  wf: HiveWorkflow;
  paletteKind: WfNodeKind | null;
  setPaletteKind: Dispatch<SetStateAction<WfNodeKind | null>>;
  inspectorOpen: boolean;
  setInspectorOpen: Dispatch<SetStateAction<boolean>>;
  fullscreen?: boolean;
  onToggleFullscreen: () => void;
}

function BuilderCanvasArea({
  wf,
  paletteKind,
  setPaletteKind,
  inspectorOpen,
  setInspectorOpen,
  fullscreen,
  onToggleFullscreen,
}: AreaProps) {
  return (
    <div className="flex min-h-0 flex-1">
      <div className="relative min-w-0 flex-1">
        <WorkflowCanvas wf={wf} fullscreen={fullscreen} onToggleFullscreen={onToggleFullscreen} />
        <AnimatePresence>
          {paletteKind && <NodePalette kind={paletteKind} onClose={() => setPaletteKind(null)} />}
        </AnimatePresence>
      </div>

      {/* Inspector — collapsible */}
      <div className="relative flex shrink-0">
        <button
          onClick={() => setInspectorOpen((o) => !o)}
          title={inspectorOpen ? "Collapse inspector" : "Expand inspector"}
          className="absolute left-0 top-3 z-20 grid h-7 w-7 -translate-x-1/2 place-items-center rounded-lg border border-app glass text-app-2 shadow transition hover:text-app"
        >
          {inspectorOpen ? <PanelRightClose size={15} /> : <PanelRightOpen size={15} />}
        </button>
        <motion.div
          animate={{ width: inspectorOpen ? 320 : 0 }}
          transition={{ type: "spring", stiffness: 420, damping: 38 }}
          className={`h-full shrink-0 overflow-hidden bg-surface/30 ${inspectorOpen ? "border-l border-app" : ""}`}
        >
          <div className="h-full w-[320px]">
            <NodeInspector wf={wf} />
          </div>
        </motion.div>
      </div>
    </div>
  );
}

function FullscreenHeader({ wf, onAdd, onExit }: { wf: HiveWorkflow; onAdd: (k: WfNodeKind) => void; onExit: () => void }) {
  const m = WF_STATUS_META[wf.status];
  return (
    <div className="flex shrink-0 items-center gap-3 border-b border-app bg-surface/40 px-4 py-2.5 backdrop-blur-xl">
      <div className="grid h-8 w-8 place-items-center rounded-lg text-white shadow" style={{ background: "linear-gradient(135deg, #f43f5e, #ec4899)" }}>
        <Maximize2 size={15} />
      </div>
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <span className="truncate text-sm font-semibold text-app">{wf.name}</span>
          <Pill color={m.color} dot={wf.status === "active"} pulse={wf.status === "active"}>{m.label}</Pill>
          <RiskBadge risk={wf.risk} />
        </div>
        <div className="text-[11px] text-app-2">Fullscreen workflow view · {wf.nodes.length} steps</div>
      </div>
      <div className="ml-auto flex items-center gap-1.5">
        <span className="mr-1 text-[10px] font-semibold uppercase tracking-wider text-app-2">Add</span>
        <AddBtn color="#f43f5e" label="Hook" onClick={() => onAdd("hook")} />
        <AddBtn color="#3b82f6" label="Process" onClick={() => onAdd("process")} />
        <AddBtn color="#10b981" label="Publish" onClick={() => onAdd("publish")} />
        <div className="mx-1 h-5 w-px bg-app" />
        <button onClick={onExit} className="flex items-center gap-1.5 rounded-lg bg-rose-500 px-3 py-1.5 text-xs font-semibold text-white transition hover:brightness-110">
          <Minimize2 size={14} /> Back to normal view
        </button>
      </div>
    </div>
  );
}

/* ── Top bar ───────────────────────────────────────────────────────────────── */

function TopBar({ wf, onAdd }: { wf: HiveWorkflow; onAdd: (k: WfNodeKind) => void }) {
  const renameWorkflow = useWorkflow((s) => s.renameWorkflow);
  const setDescription = useWorkflow((s) => s.setDescription);
  const setEnv = useWorkflow((s) => s.setEnv);
  const fireTest = useWorkflow((s) => s.fireTest);
  const saveDraft = useWorkflow((s) => s.saveDraft);
  const requestPublish = useWorkflow((s) => s.requestPublish);
  const pauseWorkflow = useWorkflow((s) => s.pauseWorkflow);
  const duplicateWorkflow = useWorkflow((s) => s.duplicateWorkflow);
  const openApp = useHive((s) => s.openApp);
  const entitlements = useHive((s) => s.entitlements);
  const openProductPreview = useHive((s) => s.openProductPreview);
  const setWorkflowFilter = useExecution((s) => s.setWorkflowFilter);

  const m = WF_STATUS_META[wf.status];
  // Activation gate: workflows with steps from a non-enabled product render
  // and test fine, but Publish becomes the Early-Access demand-capture CTA.
  const gatedProducts = workflowGatedProducts(wf, entitlements);
  const gatedName = gatedProducts.length ? APPS[gatedProducts[0]].name : null;

  const viewRuns = () => {
    setWorkflowFilter(wf.id);
    openApp("execution");
  };

  return (
    <div className="shrink-0 border-b border-app bg-surface/40 backdrop-blur-xl">
      <div className="flex items-center gap-3 px-4 py-2.5">
        <div className="grid h-9 w-9 place-items-center rounded-xl text-white shadow-lg" style={{ background: "linear-gradient(135deg, #f43f5e, #ec4899)" }}>
          <Zap size={18} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <input
              value={wf.name}
              onChange={(e) => renameWorkflow(wf.id, e.target.value)}
              className="min-w-0 max-w-[360px] flex-1 rounded-md bg-transparent text-sm font-semibold text-app outline-none hover:bg-surface-2/50 focus:bg-surface-2/60 px-1 py-0.5"
            />
            <Pill color={m.color} dot={wf.status === "active"} pulse={wf.status === "active"}>{m.label}</Pill>
            {wf.isSample && <SampleBadge />}
            <RiskBadge risk={wf.risk} />
            <span className="text-[11px] text-app-2">{wf.version}</span>
            <span className="flex items-center gap-1 text-[11px] text-emerald-500">
              <span className="relative flex h-1.5 w-1.5"><span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-500 opacity-60" /><span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-500" /></span>
              Autosaved
            </span>
          </div>
          <input
            value={wf.description}
            onChange={(e) => setDescription(wf.id, e.target.value)}
            className="mt-0.5 w-full max-w-[640px] rounded-md bg-transparent px-1 text-[11px] text-app-2 outline-none hover:bg-surface-2/40 focus:bg-surface-2/50"
          />
        </div>

        <Segmented
          value={wf.env}
          onChange={(v) => setEnv(wf.id, v)}
          accent={wf.env === "production" ? "#f43f5e" : "#22c55e"}
          options={[
            { value: "sandbox", label: "Sandbox", icon: <Cog size={11} /> },
            { value: "production", label: "Production", icon: <Rocket size={11} /> },
          ]}
        />
      </div>

      <div className="flex items-center gap-1.5 border-t border-app px-4 py-2">
        {/* add node group */}
        <span className="mr-1 text-[10px] font-semibold uppercase tracking-wider text-app-2">Add</span>
        <AddBtn color="#f43f5e" label="Hook" onClick={() => onAdd("hook")} />
        <AddBtn color="#3b82f6" label="Process" onClick={() => onAdd("process")} />
        <AddBtn color="#10b981" label="Publish" onClick={() => onAdd("publish")} />

        <div className="mx-1.5 h-5 w-px bg-app" />

        <ActionBtn icon={<Play size={13} />} label="Run Test" onClick={fireTest} />
        <ActionBtn icon={<Save size={13} />} label="Save Draft" onClick={() => saveDraft(wf.id)} />
        {gatedProducts.length === 0 ? (
          <ActionBtn icon={<Rocket size={13} />} label="Publish" primary onClick={() => requestPublish(wf.id)} />
        ) : (
          <button
            onClick={() => openProductPreview(gatedProducts[0])}
            title={`This workflow contains ${gatedName} steps — request early access to activate it`}
            className="flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-[11px] font-semibold transition hover:brightness-110"
            style={{
              borderColor: APPS[gatedProducts[0]].accent,
              color: APPS[gatedProducts[0]].accent,
              background: `${APPS[gatedProducts[0]].accent}14`,
            }}
          >
            <Lock size={12} /> Requires {gatedName} · Early Access
          </button>
        )}
        <ActionBtn icon={<Pause size={13} />} label={wf.status === "paused" ? "Resume" : "Pause"} onClick={() => pauseWorkflow(wf.id)} />
        <ActionBtn icon={<Copy size={13} />} label="Duplicate" onClick={() => duplicateWorkflow(wf.id)} />
        <ActionBtn icon={<History size={13} />} label="View Runs" onClick={viewRuns} />

        <span className="ml-auto flex items-center gap-1 text-[11px] text-app-2"><CircleDot size={11} /> {wf.owner}</span>
      </div>
    </div>
  );
}

function AddBtn({ color, label, onClick }: { color: string; label: string; onClick: () => void }) {
  return (
    <button onClick={onClick} className="flex items-center gap-1 rounded-lg border px-2.5 py-1 text-[11px] font-medium transition hover:brightness-110" style={{ color, borderColor: `${color}44`, background: `${color}12` }}>
      <span className="text-base leading-none">+</span> {label}
    </button>
  );
}

function ActionBtn({ icon, label, onClick, primary }: { icon: React.ReactNode; label: string; onClick: () => void; primary?: boolean }) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-[11px] font-medium transition ${
        primary ? "bg-rose-500 text-white hover:brightness-110" : "border border-app bg-surface-2/50 text-app-1 hover:border-app-strong"
      }`}
    >
      {icon} {label}
    </button>
  );
}

/* ── Publish confirmation ─────────────────────────────────────────────────── */

function PublishModal() {
  const id = useWorkflow((s) => s.publishWorkflowId);
  const workflows = useWorkflow((s) => s.workflows);
  const cancel = useWorkflow((s) => s.cancelPublish);
  const confirm = useWorkflow((s) => s.confirmPublish);
  const entitlements = useHive((s) => s.entitlements);
  const openProductPreview = useHive((s) => s.openProductPreview);
  const wf = workflows.find((w) => w.id === id) ?? null;

  if (!wf) return null;
  const issues = validateWorkflow(wf);
  const errors = issues.filter((i) => i.level === "error");
  const fp = permissionFootprint(wf);
  const adminRequired = wf.env === "production" && (wf.risk === "restricted" || wf.risk === "high" || fp.deploy);
  // Reachable via Cmd+K even when the top-bar button is swapped — the server
  // rejects too, but blocking here gives the clear in-context message.
  const gatedProducts = workflowGatedProducts(wf, entitlements);
  const blocked = errors.length > 0 || gatedProducts.length > 0;

  const caps = [
    { label: "Reads from", v: `${fp.reads.length} scopes`, on: fp.reads.length > 0 },
    { label: "Writes to", v: `${fp.writes.length} scopes`, on: fp.writes.length > 0 },
    { label: "Posts externally", v: fp.external ? "Yes" : "No", on: fp.external },
    { label: "Modifies code", v: fp.code ? "Yes" : "No", on: fp.code },
    { label: "Creates PRs", v: fp.pr ? "Yes" : "No", on: fp.pr },
    { label: "Triggers deployments", v: fp.deploy ? "Yes" : "No", on: fp.deploy },
    { label: "Spends budget", v: fp.budget ? "Yes" : "No", on: fp.budget },
  ];

  return (
    <Modal open={!!id} onClose={cancel} title={`Publish “${wf.name}”`} subtitle={`to ${wf.env}`} icon={<Send size={16} />} accent="#f43f5e" width={460}>
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <RiskBadge risk={wf.risk} />
          <span className="text-[11px] text-app-2">This workflow will be enabled and start triggering on its hook.</span>
        </div>

        <div>
          <div className="mb-1.5 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-app-2"><Lock size={12} /> Permissions preview</div>
          <div className="grid grid-cols-2 gap-x-4 gap-y-1 rounded-xl border border-app bg-surface-2/40 p-3">
            {caps.map((c) => (
              <div key={c.label} className="flex items-center justify-between text-[11px]">
                <span className="text-app-1">{c.label}</span>
                <span className={c.on ? "font-medium text-amber-500" : "text-app-2"}>{c.v}</span>
              </div>
            ))}
          </div>
        </div>

        {gatedProducts.length > 0 && (
          <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-3">
            <div className="flex items-center gap-1.5 text-[11px] font-semibold text-amber-500">
              <Lock size={13} /> Requires {gatedProducts.map((p) => APPS[p].name).join(", ")} · Early Access
            </div>
            <p className="mt-1 text-[11px] leading-relaxed text-app-1">
              This workflow contains steps from products that aren't enabled for your
              organization. It stays fully editable and testable — activation unlocks with access.
            </p>
            <button
              onClick={() => {
                cancel();
                openProductPreview(gatedProducts[0]);
              }}
              className="mt-2 flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-[11px] font-semibold text-black transition hover:brightness-105"
              style={{ background: APPS[gatedProducts[0]].accent }}
            >
              Request early access
            </button>
          </div>
        )}

        {errors.length > 0 && (
          <div className="rounded-xl border border-rose-500/30 bg-rose-500/5 p-3">
            <div className="flex items-center gap-1.5 text-[11px] font-semibold text-rose-500"><ShieldAlert size={13} /> {errors.length} blocking error{errors.length > 1 ? "s" : ""}</div>
            <ul className="mt-1.5 space-y-1">
              {errors.slice(0, 4).map((e, i) => (
                <li key={i} className="text-[11px] text-app-1">· {e.text}</li>
              ))}
            </ul>
          </div>
        )}

        {!blocked && adminRequired && (
          <div className="flex items-start gap-2 rounded-xl border border-amber-500/30 bg-amber-500/5 p-3 text-[11px] text-amber-500">
            <Lock size={14} className="mt-0.5 shrink-0" />
            <span>Admin approval required before publishing — this workflow performs {wf.risk === "restricted" ? "restricted" : "high-risk"} actions{fp.deploy ? " including deployments" : ""} in production.</span>
          </div>
        )}

        <div className="flex items-center justify-end gap-2 pt-1">
          <button onClick={cancel} className="rounded-lg border border-app bg-surface-2/60 px-3 py-2 text-xs font-medium text-app-1 transition hover:border-app-strong">Cancel</button>
          <button
            disabled={blocked}
            onClick={() => confirm(wf.id)}
            className={`flex items-center gap-1.5 rounded-lg px-3.5 py-2 text-xs font-semibold transition ${
              blocked ? "cursor-not-allowed bg-surface-2 text-app-2" : adminRequired ? "bg-amber-500 text-black hover:brightness-105" : "bg-rose-500 text-white hover:brightness-110"
            }`}
          >
            {adminRequired ? <Lock size={13} /> : <Check size={13} />}{" "}
            {gatedProducts.length > 0
              ? `Requires ${APPS[gatedProducts[0]].name}`
              : blocked
                ? "Resolve errors first"
                : adminRequired
                  ? "Request admin approval"
                  : "Publish to " + wf.env}
          </button>
        </div>
      </div>
    </Modal>
  );
}
